import React from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { SimpleTextInput } from "components/FormTextInput";
import { SimpleSelectInput } from "components/FormSelectInput";
import { RestartAlt, Save } from "@mui/icons-material";

const BET_BUILDER_MARKET_LABELS: Record<string, string> = {
  FINAL_RESULT_ANYTIME_GOALSCORER: "Final Result & Anytime Goalscorer",
  MATCH_RESULT_UNDER_OVER: "Match Result & Over/Under",
  BTTS_UNDER_OVER: "Both Teams to Score & Over/Under",
};

const useStyles = makeStyles((theme) => ({
  overallRtpContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(3),
    backgroundColor: "#f8f9fa",
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(3),
    border: "1px solid #e9ecef",
  },
  overallRtpTitle: {
    fontWeight: "bold",
    color: "#2e7d32",
    marginBottom: theme.spacing(0.5),
  },
  overallRtpValue: {
    fontWeight: "bold",
    color: "#4caf50",
  },
  textAlignRight: {
    textAlign: "right",
  },
  marginBottom4px: {
    marginBottom: "4px",
  },
  marginBottom3: {
    marginBottom: theme.spacing(3),
  },
  sectionSubtitle: {
    marginBottom: theme.spacing(2),
    color: "#666666",
  },
  sectionSaveButton: {
    marginTop: theme.spacing(2),
    backgroundColor: "#4caf50",
    color: "white",
    fontWeight: "bold",
    "&:hover": {
      backgroundColor: "#45a049",
    },
    "&:disabled": {
      backgroundColor: "#e0e0e0",
      color: "#9e9e9e",
    },
  },
}));

const MarginRTPSection = ({
  operatorDetails,
  options,
  errors,
  onMarketChange,
  getValidationStatus,
  defaultMarginData,
  canEditMargins,
  onSave,
  loading,
  marketToggles,
  onMarketToggleChange,
  marketOrder,
  onMarketOrderChange,
  onMarketOrderBlur,
  onResetMarketOrder,
  lockToggles,
  onLockToggleChange,
}) => {
  const styles = useStyles();
  const isLocked =
    operatorDetails?.operatorMargins?.EXTRA_DATA?.isMarginEditingAllowed;

  return (
    <Paper
      elevation={1}
      sx={{
        p: 3,
        mb: 3,
        borderRadius: 2,
        backgroundColor: isLocked ? "#ffffff" : "#f5f5f5",
        border: isLocked ? "1px solid #4caf50" : "1px solid #e0e0e0",
        borderTop: isLocked ? "4px solid #4caf50" : "4px solid #ff9800",
        opacity: isLocked ? 1 : 0.7,
        transition: "all 0.3s ease",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: "-12px",
          left: "50%",
          transform: "translateX(-50%)",
          width: 0,
          height: 0,
          borderLeft: "8px solid transparent",
          borderRight: "8px solid transparent",
          borderBottom: isLocked ? "8px solid #4caf50" : "8px solid #ff9800",
        },
      }}
    >
      <Box className={styles.marginBottom3}>
        <Box display="flex" alignItems="center" gap={2} mb={1}>
          <Typography
            variant="h6"
            sx={{
              color: isLocked ? "#2e7d32" : "#757575",
              fontWeight: "bold",
            }}
          >
            📊 Margin & RTP Configuration
          </Typography>
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 3,
              backgroundColor: isLocked ? "#e8f5e8" : "#fff3e0",
              border: isLocked ? "1px solid #4caf50" : "1px solid #ff9800",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: isLocked ? "#2e7d32" : "#f57c00",
                fontWeight: "bold",
                fontSize: "11px",
              }}
            >
              {isLocked ? "EDITABLE" : "LOCKED"}
            </Typography>
          </Box>
        </Box>
        <Typography
          variant="body2"
          color="textSecondary"
          className={styles.sectionSubtitle}
        >
          {isLocked
            ? "Configure market margins and view calculated Return to Player percentages"
            : "Enable editing mode above to modify market margins and RTP values"}
        </Typography>
      </Box>

      {/* Overall RTP Display */}
      <Box className={styles.overallRtpContainer}>
        <Box>
          <Typography variant="h6" className={styles.overallRtpTitle}>
            Overall Return to Player
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Real-time calculation based on all market RTPs
          </Typography>
        </Box>
        <Box className={styles.textAlignRight}>
          <Typography
            variant="body2"
            color="textSecondary"
            className={styles.marginBottom4px}
          >
            Overall RTP
          </Typography>
          <Typography variant="h4" className={styles.overallRtpValue}>
            {operatorDetails?.operatorRtp?.OVER_ALL || "0.00"}%
          </Typography>
        </Box>
      </Box>

      {/* Table Layout */}
      <TableContainer component={Paper} style={{ maxHeight: 600 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ fontWeight: "bold", minWidth: 150 }}>
                Market
              </TableCell>
              <TableCell style={{ fontWeight: "bold", width: 90 }}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  Order
                  <Tooltip title="Reset all order values">
                    <span>
                      <IconButton
                        size="small"
                        onClick={onResetMarketOrder}
                        disabled={!isLocked || !canEditMargins}
                      >
                        <RestartAlt fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell
                style={{ fontWeight: "bold", width: 120, color: "red" }}
              >
                Range (%)
              </TableCell>
              <TableCell style={{ fontWeight: "bold", width: 120 }}>
                Margin (%)
              </TableCell>
              <TableCell style={{ fontWeight: "bold", width: 100 }}>
                RTP (%)
              </TableCell>
              <TableCell style={{ fontWeight: "bold", width: 200 }}>
                Validation Status
              </TableCell>
              <TableCell style={{ fontWeight: "bold", width: 220 }}>
                Operator Override
              </TableCell>
              <TableCell style={{ fontWeight: "bold", width: 220 }}>
                Auto-Lock Override
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {options?.bettingMarkets
              ?.filter((item) => item !== "OVER_ALL")
              ?.map((market) => {
                const marginValue = operatorDetails?.operatorMargins?.[market];
                const rtpValue = operatorDetails?.operatorRtp?.[market];
                const hasError = errors[market];
                const isDisabled = !isLocked || !canEditMargins;

                // Get min/max from default margin data
                const defaultMarginInfo =
                  defaultMarginData?.operatorMargins?.EXTRA_DATA?.[market];
                const minRange = defaultMarginInfo?.min || 0;
                const maxRange = defaultMarginInfo?.max || 100;

                // Don't render if operatorMargins is null
                if (!operatorDetails?.operatorMargins) {
                  return null;
                }

                return (
                  <TableRow
                    key={market}
                    style={{
                      backgroundColor: hasError ? "#ffebee" : "inherit",
                    }}
                  >
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontWeight: "bold" }}
                      >
                        {market.replace(/_/g, " ")}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <SimpleTextInput
                        type="number"
                        name={market}
                        value={
                          marketOrder?.[market] === undefined
                            ? ""
                            : marketOrder[market]
                        }
                        onChange={onMarketOrderChange}
                        onBlur={onMarketOrderBlur}
                        disabled={isDisabled}
                        inputProps={{ min: 1, step: 1 }}
                        size="small"
                        style={{ width: "100px" }}
                        placeholder="Order"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {minRange} - {maxRange}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <SimpleTextInput
                        type="number"
                        name={market}
                        value={
                          marginValue === null || marginValue === undefined
                            ? ""
                            : marginValue
                        }
                        onChange={(e) => onMarketChange(e, "operatorMargins")}
                        disabled={isDisabled}
                        inputProps={{
                          min: minRange,
                          max: maxRange,
                          step: 0.1,
                        }}
                        error={!!hasError}
                        size="small"
                        style={{ width: "100px" }}
                        placeholder="Margin"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontWeight: "bold", color: "#4caf50" }}
                      >
                        {rtpValue || "0.00"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const validationStatus = getValidationStatus(
                          marginValue,
                          market,
                          hasError,
                        );

                        // Determine color based on status type
                        let statusColor;
                        if (validationStatus === "true") {
                          statusColor = "#4caf50"; // Green for valid
                        } else if (validationStatus === "NA") {
                          statusColor = "#9e9e9e"; // Gray for no data
                        } else if (validationStatus === "false") {
                          statusColor = "#f44336"; // Red for false
                        } else {
                          // Error message case
                          statusColor = "#f44336"; // Red for error messages
                        }

                        return (
                          <Typography
                            variant="caption"
                            style={{
                              fontSize: "11px",
                              color: statusColor,
                              fontWeight: "bold",
                            }}
                          >
                            {validationStatus}
                          </Typography>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {BET_BUILDER_MARKET_LABELS[market]
                        ? (() => {
                            const current = marketToggles?.[market];
                            const currentValue =
                              current === undefined
                                ? "inherit"
                                : current
                                  ? "on"
                                  : "off";

                            return (
                              <SimpleSelectInput
                                name={`marketToggles.${market}`}
                                value={currentValue}
                                onChange={(e) =>
                                  onMarketToggleChange(market, e.target.value)
                                }
                                disabled={isDisabled}
                                options={[
                                  {
                                    id: "inherit",
                                    label: "Use global default",
                                  },
                                  { id: "on", label: "Force on" },
                                  { id: "off", label: "Force off" },
                                ]}
                              />
                            );
                          })()
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const currentLock = lockToggles?.[market];
                        const currentLockValue =
                          currentLock === undefined
                            ? "inherit"
                            : currentLock
                              ? "on"
                              : "off";

                        return (
                          <SimpleSelectInput
                            name={`lockToggles.${market}`}
                            value={currentLockValue}
                            onChange={(e) =>
                              onLockToggleChange(market, e.target.value)
                            }
                            disabled={isDisabled}
                            options={[
                              { id: "inherit", label: "Use global default" },
                              { id: "on", label: "Force auto-lock on" },
                              { id: "off", label: "Force auto-lock off" },
                            ]}
                          />
                        );
                      })()}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Section Save Button */}
      {isLocked && canEditMargins && onSave && (
        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={onSave}
            disabled={loading || Object.keys(errors).length > 0}
            startIcon={<Save />}
            className={styles.sectionSaveButton}
          >
            {loading ? "Saving..." : "Save Margins & RTP"}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default MarginRTPSection;
